import { OPS_PRICE_INCREASE_LIMIT_INR } from '@utazzo/shared';
import { UzButton, UzCard, UzDialog, UzSpinner, UzTextarea } from '@utazzo/ui';
import { Check, X } from 'lucide-react';
import { useState } from 'react';

import { useDecideOpsApproval, useOpsApprovals } from '../use-ops-cases';

import type { OpsItemQueueRow } from '@utazzo/shared';

/**
 * Two kinds of approvals, one list:
 *  - Price increases (Admin + the ops executive's TL): the supplier price
 *    pushed the lead's total increase over INR 500. Approve sends the fund
 *    request on to Accounts; reject sends it back to ops.
 *  - Itinerary changes (Admin only): ops could not book an item. Approve sends
 *    it to the seller to change with the customer; reject puts it back to
 *    "to book" for ops to find another way.
 */

const inr = (n: number | null): string =>
  n === null
    ? '-'
    : `INR ${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(n)}`;
const money = (n: number | null, c: string | null): string =>
  n === null ? '-' : `${c ?? 'INR'} ${new Intl.NumberFormat('en-IN').format(n)}`;
const when = (d: Date | null): string =>
  d
    ? d.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '-';

export const OpsApprovalsPanel = (): React.JSX.Element => {
  const query = useOpsApprovals(true);
  const decide = useDecideOpsApproval();
  const [rejecting, setRejecting] = useState<OpsItemQueueRow | null>(null);
  const [note, setNote] = useState('');
  const rows = query.data ?? [];

  if (query.isLoading) {
    return (
      <UzCard>
        <div className="flex items-center gap-2 py-4 text-sm text-zinc-500">
          <UzSpinner size="sm" /> Loading approvals…
        </div>
      </UzCard>
    );
  }
  if (rows.length === 0) {
    return (
      <UzCard>
        <p className="py-6 text-center text-sm text-zinc-500">
          Nothing to approve. Price increases up to INR {OPS_PRICE_INCREASE_LIMIT_INR} per lead go
          straight to Accounts; items ops cannot book show up here for the itinerary-change OK.
        </p>
      </UzCard>
    );
  }

  return (
    <div className="space-y-3">
      {rows.map((r) => (
        <UzCard key={`${r.opsCaseId}:${r.componentId}`}>
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div className="min-w-0 space-y-1 text-sm">
              <div className="font-medium text-zinc-900">
                {r.customerName} <span className="text-zinc-400">· {r.leadReference}</span>
                {r.status === 'AMENDMENT_APPROVAL' ? (
                  <span className="ml-2 rounded-md bg-rose-50 px-1.5 py-0.5 text-[11px] font-semibold text-rose-700 ring-1 ring-rose-200 ring-inset">
                    Itinerary change
                  </span>
                ) : r.approvalReason === 'OVER_RECEIPTS' ? (
                  <span className="ml-2 rounded-md bg-red-50 px-1.5 py-0.5 text-[11px] font-semibold text-red-700 ring-1 ring-red-200 ring-inset">
                    More than the customer paid
                  </span>
                ) : (
                  <span className="ml-2 rounded-md bg-orange-50 px-1.5 py-0.5 text-[11px] font-semibold text-orange-700 ring-1 ring-orange-200 ring-inset">
                    Price increase
                  </span>
                )}
              </div>
              <div className="text-zinc-700">
                {r.itemTitle}
                {r.supplierName ? <span className="text-zinc-500"> · {r.supplierName}</span> : null}
              </div>
              {r.status === 'AMENDMENT_APPROVAL' ? (
                <>
                  <div className="text-xs text-rose-800">Not available: {r.amendmentReason}</div>
                  <div className="text-xs text-zinc-500">
                    Asked by {r.opsOwnerName ?? 'operations'} · {when(r.amendmentRequestedAt)} ·
                    approving sends it to the seller to change with the customer; rejecting puts it
                    back to "to book".
                  </div>
                </>
              ) : (
                <>
                  <div className="grid gap-x-6 gap-y-1 text-xs text-zinc-600 sm:grid-cols-4">
                    <div>
                      <div className="text-[10px] font-semibold text-zinc-400 uppercase">
                        Quoted
                      </div>
                      {inr(r.quotedInr)}
                    </div>
                    <div>
                      <div className="text-[10px] font-semibold text-zinc-400 uppercase">Asked</div>
                      {money(r.fundAmount, r.fundCurrency)}
                      {r.fundCurrency && r.fundCurrency !== 'INR' ? ` (${inr(r.fundInr)})` : ''}
                    </div>
                    <div>
                      <div className="text-[10px] font-semibold text-zinc-400 uppercase">
                        This item
                      </div>
                      <span className="text-orange-700">
                        {r.priceDeltaInr !== null && r.priceDeltaInr > 0 ? '+' : ''}
                        {inr(r.priceDeltaInr)}
                      </span>
                    </div>
                    <div>
                      <div className="text-[10px] font-semibold text-zinc-400 uppercase">
                        Lead total increase
                      </div>
                      <span className="font-semibold text-orange-800">
                        {inr(r.approvalIncreaseInr)}
                      </span>
                    </div>
                  </div>
                  {r.approvalReason === 'OVER_RECEIPTS' && r.leadMoney ? (
                    <div className="text-xs text-red-800">
                      Suppliers asked {inr(r.leadMoney.committedInr)} against{' '}
                      {inr(r.leadMoney.receivedInr)} received from the customer
                      {r.leadMoney.saleInr !== null
                        ? ` (sold for ${inr(r.leadMoney.saleInr)}, ${inr(r.leadMoney.balanceInr ?? 0)} still owed)`
                        : ''}
                      {r.approvalShortfallInr !== null
                        ? ` - short by ${inr(r.approvalShortfallInr)}. Approving lets Accounts pay before the customer's money is in.`
                        : '.'}
                    </div>
                  ) : null}
                  <div className="text-xs text-zinc-500">
                    Asked by {r.requestedByName ?? '-'} · {when(r.fundRequestedAt)}
                    {r.note ? ` · "${r.note}"` : ''}
                  </div>
                </>
              )}
            </div>
            <div className="flex shrink-0 gap-2">
              <UzButton
                size="sm"
                variant="outline"
                className="text-red-600"
                onClick={() => {
                  setNote('');
                  setRejecting(r);
                }}
              >
                <X className="mr-1.5 h-3.5 w-3.5" /> Reject
              </UzButton>
              <UzButton
                size="sm"
                loading={
                  decide.isPending &&
                  decide.variables?.componentId === r.componentId &&
                  decide.variables?.input.approve
                }
                onClick={() =>
                  decide.mutate({
                    caseId: r.opsCaseId,
                    componentId: r.componentId,
                    input: { approve: true },
                  })
                }
              >
                <Check className="mr-1.5 h-3.5 w-3.5" /> Approve
              </UzButton>
            </div>
          </div>
        </UzCard>
      ))}

      <UzDialog
        open={rejecting !== null}
        onOpenChange={(o) => !o && setRejecting(null)}
        title={
          rejecting?.status === 'AMENDMENT_APPROVAL'
            ? 'Reject this itinerary change?'
            : 'Reject this price increase?'
        }
        description={
          rejecting?.status === 'AMENDMENT_APPROVAL'
            ? 'The item goes back to "to book"; operations is told to find another way.'
            : 'Operations is told to re-quote or send the item for amendment.'
        }
        footer={
          <div className="flex justify-end gap-2">
            <UzButton variant="ghost" onClick={() => setRejecting(null)}>
              Cancel
            </UzButton>
            <UzButton
              variant="destructive"
              loading={decide.isPending}
              onClick={() =>
                rejecting &&
                decide.mutate(
                  {
                    caseId: rejecting.opsCaseId,
                    componentId: rejecting.componentId,
                    input: { approve: false, ...(note.trim() ? { note: note.trim() } : {}) },
                  },
                  { onSuccess: () => setRejecting(null) },
                )
              }
            >
              Reject
            </UzButton>
          </div>
        }
      >
        <UzTextarea
          rows={3}
          placeholder="Why (optional) - e.g. try another supplier"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </UzDialog>
    </div>
  );
};
